import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import Header from './Header';
import Footer from './Footer';
import { TemplateRenderer } from './PortfolioTemplates';
import './PortfolioPage.css';

const TEMPLATES = [
  { id: 'minimal',   name: 'Minimal',   desc: 'Clean & typographic',  bg: '#f8f8f8',  accent: '#111',     text: '#111' },
  { id: 'dark',      name: 'Dark Pro',  desc: 'Sleek glassmorphism',  bg: '#0a0a12',  accent: '#e0a3ff',  text: '#e0a3ff' },
  { id: 'creative',  name: 'Creative',  desc: 'Purple sidebar',       bg: 'linear-gradient(180deg,#1e1148,#6d28d9)', accent: '#fff', text: '#fff' },
  { id: 'executive', name: 'Executive', desc: 'Two-column corporate', bg: '#1e2a4a',  accent: '#4fc3f7',  text: '#fff' },
  { id: 'neon',      name: 'Neon',      desc: 'Cyberpunk tech style', bg: '#050a10',  accent: '#00e5ff',  text: '#00e5ff' },
  { id: 'elegant',   name: 'Elegant',   desc: 'Serif & gold accents', bg: '#faf8f5',  accent: '#b8860b',  text: '#2c1810' },
];

const PortfolioPage = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

  const [profile, setProfile]               = useState(null);
  const [selectedTemplate, setSelected]     = useState('minimal');
  const [portfolioData, setPortfolioData]   = useState(null);
  const [saving, setSaving]                 = useState(false);
  const [loading, setLoading]               = useState(true);
  const [copied, setCopied]                 = useState(false);
  const [message, setMessage]               = useState('');
  const [showSavePopup, setShowSavePopup]   = useState(false);
  const openPricingRef                      = useRef(null);

  const hasAccess = user?.subscription_status === 'active' || user?.subscription_status === 'cancelling' || !!user?.subscription_plan;
  const publicLink = portfolioData?.slug
    ? `${window.location.origin}/p/${portfolioData.slug}`
    : null;

  useEffect(() => {
    if (!user) { navigate('/'); return; }
    fetchData();
  }, [user]); // eslint-disable-line

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    try {
      const [profileRes, portfolioRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/profile`,      { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE_URL}/api/portfolio/me`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const profileData  = await profileRes.json();
      const portfolioInfo = await portfolioRes.json();

      if (profileData.profile)       setProfile(profileData.profile);
      if (portfolioInfo.portfolio) {
        setPortfolioData(portfolioInfo.portfolio);
        setSelected(portfolioInfo.portfolio.template || 'minimal');
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      const res  = await fetch(`${API_BASE_URL}/api/portfolio/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ template: selectedTemplate }),
      });
      const data = await res.json();
      if (res.ok) {
        setPortfolioData(data.portfolio);
        // Auto-update profile portfolio URL with the public link
        if (data.portfolio?.slug) {
          const publicUrl = `${window.location.origin}/p/${data.portfolio.slug}`;
          fetch(`${API_BASE_URL}/api/profile/portfolio-url`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
            body: JSON.stringify({ portfolioUrl: publicUrl }),
          }).catch(() => {});
        }
        if (hasAccess) {
          setShowSavePopup(true);
        } else {
          openPricingRef.current?.();
        }
      } else {
        setMessage('Failed to save.');
      }
    } catch (e) {
      setMessage('Network error.');
    } finally {
      setSaving(false);
    }
  };

  const copyLink = () => {
    if (!publicLink) return;
    navigator.clipboard.writeText(publicLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return (
    <div className="neural-glass-homepage">
      <div className="neural-background" />
      <div className="geometric-shapes">
        <div className="shape shape-1" /><div className="shape shape-2" />
        <div className="shape shape-3" /><div className="shape shape-4" />
      </div>
      <div className="pf-page">
        <Header setShowAuthModal={() => {}} openPricingRef={openPricingRef} />
        <div className="pf-loading"><div className="pf-spinner" /><p>Loading…</p></div>
        <Footer />
      </div>
    </div>
  );

  return (
    <div className="neural-glass-homepage">
      <div className="neural-background" />
      <div className="geometric-shapes">
        <div className="shape shape-1" /><div className="shape shape-2" />
        <div className="shape shape-3" /><div className="shape shape-4" />
      </div>

      <div className="pf-page">
        <Header setShowAuthModal={() => {}} openPricingRef={openPricingRef} />

        {/* ── Hero ── */}
        <div className="pf-hero">
          <div className="li-hero-badge">🚀 AI-Powered</div>
          <h1 className="pf-hero-title">AI <span className="li-gradient-text">Portfolio Builder</span></h1>
          <p className="pf-hero-sub">Pick a template, preview it live, then share your portfolio with the world.</p>
        </div>

        <div className="pf-layout">
          {/* ── Left panel ── */}
          <aside className="pf-sidebar">
            <h3 className="pf-sidebar-heading">Choose a Style</h3>

            <div className="pf-template-grid">
              {TEMPLATES.map(t => (
                <button
                  key={t.id}
                  className={`pf-template-card ${selectedTemplate === t.id ? 'pf-template-card--active' : ''}`}
                  onClick={() => setSelected(t.id)}
                >
                  <div
                    className="pf-template-thumb"
                    style={{ background: t.bg }}
                  >
                    {/* mini lines to suggest layout */}
                    <div className="pf-thumb-line" style={{ background: t.accent, width: '60%', opacity: 0.9 }} />
                    <div className="pf-thumb-line" style={{ background: t.accent, width: '40%', opacity: 0.5 }} />
                    <div className="pf-thumb-line" style={{ background: t.accent, width: '80%', opacity: 0.3 }} />
                    <div className="pf-thumb-line" style={{ background: t.accent, width: '70%', opacity: 0.3 }} />
                  </div>
                  <div className="pf-template-info">
                    <span className="pf-template-name">{t.name}</span>
                    <span className="pf-template-desc">{t.desc}</span>
                  </div>
                  {selectedTemplate === t.id && <div className="pf-template-check">✓</div>}
                </button>
              ))}
            </div>

            <div className="pf-action-box">
              {message && <p className={`pf-msg ${message.startsWith('✅') ? 'pf-msg--ok' : 'pf-msg--err'}`}>{message}</p>}
              <button className={`pf-save-btn ${saving ? 'pf-save-btn--loading' : ''}`} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : '💾 Save Portfolio'}
              </button>

              {portfolioData?.slug && hasAccess && (
                <a className="pf-preview-link" href={`/p/${portfolioData.slug}`} target="_blank" rel="noreferrer">
                  ↗ Open Full Preview
                </a>
              )}
            </div>
          </aside>

          {/* ── Preview panel ── */}
          <div className="pf-preview-panel">
            <div className="pf-preview-header">
              <span className="pf-preview-label">Live Preview — {TEMPLATES.find(t => t.id === selectedTemplate)?.name}</span>
            </div>
            <div className="pf-preview-scroll">
              {profile ? (
                <TemplateRenderer template={selectedTemplate} profile={profile} />
              ) : (
                <div className="pf-preview-empty">
                  <p>Complete your <a href="/profile">Profile</a> to see it rendered here.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Save success popup ── */}
        {showSavePopup && (
          <div className="pf-popup-overlay" onClick={() => setShowSavePopup(false)}>
            <div className="pf-popup" onClick={e => e.stopPropagation()}>
              <div className="pf-popup-icon">🎉</div>
              <h3 className="pf-popup-title">Portfolio Saved!</h3>
              <p className="pf-popup-sub">Share your portfolio link with recruiters.</p>
              <div className="pf-popup-link-row">
                <span className="pf-popup-link-text">{publicLink}</span>
                <button
                  className="pf-popup-copy-btn"
                  onClick={() => { navigator.clipboard.writeText(publicLink); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                >
                  {copied ? '✓ Copied!' : 'Copy Link'}
                </button>
              </div>
              {portfolioData?.slug && (
                <a className="pf-popup-preview" href={`/p/${portfolioData.slug}`} target="_blank" rel="noreferrer">
                  ↗ Open Full Preview
                </a>
              )}
              <button className="pf-popup-close" onClick={() => setShowSavePopup(false)}>Done</button>
            </div>
          </div>
        )}

        <Footer />
      </div>
    </div>
  );
};

export default PortfolioPage;
